// GoalsTrackerPage.js

import React, { createContext, useState, useEffect, useContext } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import { FinanceProvider, FinanceContext } from "../contexts/FinanceContext";
import {
  collection,
  addDoc,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import Navbar from "../components/Navbar";
import { useTranslation } from "react-i18next";

// =========================================================
// I. GLOBAL STYLES
// =========================================================
const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    font-family: 'Montserrat', sans-serif;
  }
  
  :root {
      --primaryBackgroundColor: #f0f5f8;
      --secondaryBackgroundColor: #fbffff;
      --thirdBackgroundColor: #ebedf0;
      --fontPrimaryColor: #414141;
      --fontSecondaryColor: #7e7e7e;
      --accentBlue: #4a90e2;
      --accentGreen: #4caf50;
      --lowGreen: #e6ffe6;
      --dangerRed: #f44336;
      --warningOrange: #ff9800;
  }
`;

// =========================================================
// II. FINANCE TOTALS HELPER
// =========================================================
const useFinanceTotals = (history) => {
  const [totals, setTotals] = useState({ totalIncome: 0, totalExpenses: 0 });

  useEffect(() => {
    if (!history) return setTotals({ totalIncome: 0, totalExpenses: 0 });

    let income = 0,
      expenses = 0;

    history.forEach((entry) => {
      const amt = Number(entry.amount || 0);
      if (entry.type === "income") income += amt;
      else if (entry.type === "spending") expenses += amt;
    });

    setTotals({ totalIncome: income, totalExpenses: expenses });
  }, [history]);

  return totals;
};

// =========================================================
// III. MOCK EXTERNAL PROVIDERS
//=========================================================
export const TasksContext = createContext();
export const HabitsContext = createContext();

const MockExternalProvider = ({ children }) => {
  const [tasks] = useState({ completedCount: 12 });
  const [habits] = useState({ checkIns: 25 });

  return (
    <TasksContext.Provider value={{ tasks }}>
      <HabitsContext.Provider value={{ habits }}>
        {children}
      </HabitsContext.Provider>
    </TasksContext.Provider>
  );
};

// =========================================================
// IV. SMART MESSAGES
// =========================================================
const getSmartMessage = (goal, t) => {
  if (goal.status === "Completed")
    return {
      text: t("goalsPage.completedMessage"),
      color: "var(--accentGreen)",
    };

  let pct;
  if (goal.type === "Milestone") {
    const completed = (goal.milestones || []).filter((m) => m.completed).length;
    const total = (goal.milestones || []).length || 1;
    pct = Math.min(100, Math.round((completed / total) * 100));
  } else {
    pct =
      goal.targetValue && goal.targetValue > 0
        ? Math.min(
            100,
            Math.round((goal.currentValue / goal.targetValue) * 100)
          )
        : 0;
  }

  if (!goal.deadline)
    return {
      text: `${pct}% ${t("goalsPage.completed")}${
        goal.type === "Milestone" ? ` (${t("goalsPage.milestones")})` : ""
      }`,
      color: "var(--accentBlue)",
    };

  const start = new Date(goal.createdAt).getTime();
  const end = new Date(goal.deadline).getTime();
  const now = Date.now();

  if (now > end)
    return { text: t("goalsPage.overdue"), color: "var(--dangerRed)" };

  const totalDuration = end - start;
  const elapsed = now - start;
  const timeProgress = totalDuration > 0 ? (elapsed / totalDuration) * 100 : 0;

  if (pct >= 95)
    return {
      text: t("goalsPage.approachingGoal"),
      color: "var(--warningOrange)",
    };
  if (pct > timeProgress + 5)
    return {
      text: t("goalsPage.aheadOfSchedule"),
      color: "var(--accentGreen)",
    };
  if (pct < timeProgress - 10)
    return { text: t("goalsPage.behindSchedule"), color: "var(--dangerRed)" };

  return { text: t("goalsPage.onTrack"), color: "var(--accentBlue)" };
};

// =========================================================
// V. GOALS CONTEXT
// =========================================================
export const GoalsContext = createContext();

const GoalsProvider = ({ children, theme }) => {
  const { currentUser } = useAuth();
  const { history } = useContext(FinanceContext);
  const { tasks } = useContext(TasksContext);
  const { habits } = useContext(HabitsContext);

  const { totalIncome, totalExpenses } = useFinanceTotals(history || []);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [justCompletedGoal, setJustCompletedGoal] = useState(null);
  const { t } = useTranslation();

  const goalsCollectionRef = () =>
    collection(db, "users", currentUser.uid, "goals");

  useEffect(() => {
    if (!currentUser) return;
    const unsub = onSnapshot(
      goalsCollectionRef(),
      (snap) => {
        const data = snap.docs.map((d) => {
          const goalData = d.data();
          const id = d.id;
          const isMilestone = goalData.type === "Milestone";
          let computedCurrentValue = Number(goalData.currentValue || 0);
          let computedStatus = goalData.status || "Active";

          if (isMilestone) {
            const milestones = goalData.milestones || [];
            const completedCount = milestones.filter((m) => m.completed).length;
            const totalCount = milestones.length;

            computedCurrentValue = completedCount;

            if (totalCount > 0 && completedCount === totalCount) {
              computedStatus = "Completed";
            } else if (goalData.status === "Completed") {
              computedStatus = "Active";
            }
          }

          const currentGoal = {
            id,
            title: goalData.title || "",
            type: goalData.type || "Personal",
            targetValue: Number(goalData.targetValue || 0),
            currentValue: computedCurrentValue,
            status: computedStatus,
            createdAt: goalData.createdAt || new Date().toISOString(),
            deadline: goalData.deadline || null,
            milestones: goalData.milestones || [],
            ...goalData,
          };

          return currentGoal;
        });

        setGoals(data);
        setLoading(false);
      },
      (err) => {
        console.error(t("goalsPage.errorListening"), err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [currentUser, t]);

  const addGoal = async (goalData) => {
    if (!currentUser) return;

    const isMilestone = goalData.type === "Milestone";

    const payload = {
      title: goalData.title || t("goalsPage.untitledGoal"),
      type: goalData.type || "Personal",
      targetValue: isMilestone
        ? (goalData.milestones || []).length
        : Number(goalData.targetValue || 0),
      currentValue: isMilestone ? 0 : Number(goalData.currentValue || 0),
      status: goalData.status || "Active",
      createdAt: goalData.createdAt || new Date().toISOString(),
      deadline: goalData.deadline || null,
      milestones: isMilestone ? goalData.milestones : [],
      createdAtServer: serverTimestamp(),
    };

    try {
      await addDoc(goalsCollectionRef(), payload);
    } catch (err) {
      console.error(t("goalsPage.errorAddGoal"), err);
    }
  };

  const updateGoal = async (id, updates) => {
    if (!currentUser) return;
    const ref = doc(db, "users", currentUser.uid, "goals", id);
    const normalized = { ...updates };

    const currentGoal = goals.find((g) => g.id === id);
    const wasCompleted = currentGoal?.status === "Completed";

    if (normalized.targetValue !== undefined)
      normalized.targetValue = Number(normalized.targetValue);
    if (normalized.currentValue !== undefined)
      normalized.currentValue = Number(normalized.currentValue);

    if (normalized.milestones !== undefined) {
      const milestones = normalized.milestones || [];
      const completedCount = milestones.filter((m) => m.completed).length;
      const totalCount = milestones.length;

      normalized.targetValue = totalCount;
      normalized.currentValue = completedCount;

      if (totalCount > 0 && completedCount === totalCount) {
        normalized.status = "Completed";
      } else if (updates.status !== "Completed") {
        normalized.status = "Active";
      }
    } else if (
      normalized.currentValue !== undefined &&
      normalized.targetValue !== undefined
    ) {
      if (normalized.currentValue >= normalized.targetValue) {
        normalized.status = "Completed";
      } else if (currentGoal?.status === "Completed") {
        normalized.status = "Active";
      }
    }

    try {
      await updateDoc(ref, normalized);

      const isNowCompleted = normalized.status === "Completed";
      if (!wasCompleted && isNowCompleted) {
        setJustCompletedGoal({
          id: id,
          title: currentGoal?.title || "Unknown Goal",
        });
      }
    } catch (err) {
      console.error(t("goalsPage.errorUpdateGoal"), err);
    }
  };

  const deleteGoal = async (id) => {
    if (!currentUser) return;
    const ref = doc(db, "users", currentUser.uid, "goals", id);
    try {
      await deleteDoc(ref);
    } catch (err) {
      console.error(t("goalsPage.errorDeleteGoal"), err);
    }
  };

  const updateManualProgress = async (id, newValue) => {
    const nv = Number(newValue || 0);
    const g = goals.find((gg) => gg.id === id);
    if (!g) return;

    const newStatus = nv >= g.targetValue ? "Completed" : "Active";

    await updateGoal(id, {
      currentValue: nv,
      status: newStatus,
      targetValue: g.targetValue,
    });
  };

  const toggleMilestone = async (goalId, milestoneIndex) => {
    const goal = goals.find((g) => g.id === goalId);
    if (!goal || goal.type !== "Milestone") return;

    const isMarkingComplete = !goal.milestones[milestoneIndex].completed;
    const newMilestones = [...goal.milestones];

    if (isMarkingComplete) {
      newMilestones[milestoneIndex].completed = true;
    } else {
      const completedIndices = newMilestones
        .map((m, idx) => (m.completed ? idx : -1))
        .filter((idx) => idx !== -1);

      const lastCompletedIndex =
        completedIndices.length > 0
          ? completedIndices[completedIndices.length - 1]
          : -1;

      if (milestoneIndex === lastCompletedIndex) {
        newMilestones[milestoneIndex].completed = false;
      } else {
        alert(t("goalsPage.uncheckWarning"));
        return;
      }
    }

    const completedCount = newMilestones.filter((m) => m.completed).length;
    const totalCount = newMilestones.length;

    const newStatus =
      totalCount > 0 && completedCount === totalCount ? "Completed" : "Active";

    await updateGoal(goalId, {
      milestones: newMilestones,
      currentValue: completedCount,
      status: newStatus,
    });
  };

  const resetDopamineSplash = () => {
    setJustCompletedGoal(null);
  };

  useEffect(() => {
    if (!currentUser || goals.length === 0) return;
    const saved = Math.max(
      0,
      Number(totalIncome || 0) - Number(totalExpenses || 0)
    );

    goals.forEach((g) => {
      if (
        !g ||
        g.type === "Personal" ||
        g.type === "Milestone" ||
        g.status === "Completed"
      )
        return;

      let computed = g.currentValue || 0;
      if (g.type === "Financial") computed = saved;
      else if (g.type === "Task")
        computed = Number((tasks && tasks.completedCount) || 0);
      else if (g.type === "Habit")
        computed = Number((habits && habits.checkIns) || 0);

      const newStatus = computed >= g.targetValue ? "Completed" : "Active";

      if (g.currentValue !== computed || g.status !== newStatus) {
        updateGoal(g.id, {
          currentValue: computed,
          status: newStatus,
          targetValue: g.targetValue,
        }).catch((err) => console.error(t("goalsPage.errorAutoUpdate"), err));
      }
    });
  }, [totalIncome, totalExpenses, tasks, habits, goals, currentUser, t]);

  return (
    <GoalsContext.Provider
      value={{
        goals,
        loading,
        addGoal,
        updateGoal,
        deleteGoal,
        updateManualProgress,
        toggleMilestone,
        justCompletedGoal,
        resetDopamineSplash,
      }}
    >
      {children}
    </GoalsContext.Provider>
  );
};

// =========================================================
// VI. UI COMPONENTS
// =========================================================

const FormContainer = styled.div`
  background: var(--secondaryBackgroundColor);
  padding: 25px;
  border-radius: 12px;
  margin-bottom: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  color: var(--fontPrimaryColor);
  width: 100%;
  transition: 0.5s;
`;

const Header = styled.header`
  margin-bottom: 0.5rem;
  padding: 0;
  margin-top: 2em;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  background: linear-gradient(90deg, var(--primaryColor), #8b5cf6);
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  color: var(--fontSecondaryColor);
  font-size: 1rem;
  margin: 0;
  padding: 0;
`;

const FormHeader = styled.h2`
  margin-bottom: 1em;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  transition: 0.5s;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--fontSecondaryColor);
  transition: 0.5s;
`;

const Input = styled.input`
  padding: 12px 15px;
  border: 1px solid var(--thirdBackgroundColor);
  background: var(--thirdBackgroundColor);
  border-radius: 8px;
  font-size: 15px;
  color: var(--fontPrimaryColor);
  font-family: "Montserrat", sans-serif;
  transition: 0.5s;
  &:focus {
    border-color: var(--accentBlue);
    outline: none;
  }
`;

const Select = styled.select`
  padding: 12px 15px;
  border: 1px solid var(--thirdBackgroundColor);
  border-radius: 8px;
  font-size: 15px;
  background: var(--thirdBackgroundColor);
  color: var(--fontSecondaryColor);
  appearance: none;
  background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23a0a0a0%22%20d%3D%22M287%20197.8L151.7%2062.5l-135%20135.2z%22%2F%3E%3C%2Fpath%3E%3C%2Fsvg%3E");
  background-repeat: no-repeat, repeat;
  background-position: right 0.7em top 50%, 0 0;
  background-size: 0.65em auto, 100%;
  font-family: "Montserrat", sans-serif;
  transition: 0.5s;

  option {
    padding: 20px;
    border-radius: 20px;
  }
`;

const SubmitButton = styled.button`
  background: var(--accentBlue);
  color: white;
  border: none;
  font-family: "Montserrat";
  padding: 14px;
  border-radius: 8px;
  cursor: pointer;
  width: 100%;
  font-size: 1rem;
  font-weight: 500;
  transition: background 0.2s, transform 0.1s;
  &:hover {
    background: #3a80c9;
  }
  &:active {
    transform: scale(0.99);
  }
`;

const MilestonesArea = styled.div`
  grid-column: 1 / -1;
  margin-top: 10px;
  padding-top: 20px;
  border-top: 1px solid var(--thirdBackgroundColor);
`;

const MilestoneInputGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
`;

const MilestoneInput = styled(Input)`
  flex-grow: 1;
  padding: 10px 15px;
`;

const AddMilestoneButton = styled(SubmitButton)`
  background: var(--accentGreen);
  width: 160px;
  padding: 10px;
  font-size: 0.9rem;
  &:hover {
    background: #3e9e47;
  }
`;

const MilestoneList = styled.ul`
  list-style: none;
  padding: 0;
  margin-top: 10px;
`;

const MilestoneItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  margin-bottom: 5px;
  font-size: 0.9rem;
  color: var(--fontPrimaryColor);
  background: var(--thirdBackgroundColor);
  border-radius: 6px;
`;

const RemoveMilestoneButton = styled.button`
  background: transparent;
  border: none;
  color: var(--dangerRed);
  cursor: pointer;
  font-size: 0.8rem;
  padding: 0 5px;
  opacity: 0.7;
  transition: opacity 0.2s;
  &:hover {
    opacity: 1;
  }
`;

// --- GoalForm Component ---
const GoalForm = () => {
  const { t } = useTranslation();
  const { addGoal } = useContext(GoalsContext);
  const [formData, setFormData] = useState({
    title: "",
    type: "Financial",
    targetValue: "",
    deadline: "",
    milestones: [],
  });
  const [newMilestoneText, setNewMilestoneText] = useState("");

  const handleTypeChange = (e) => {
    setFormData({
      ...formData,
      type: e.target.value,
      targetValue: e.target.value === "Milestone" ? "" : formData.targetValue,
    });
  };

  const handleAddMilestone = () => {
    if (newMilestoneText.trim() === "") return;
    const newMilestone = {
      title: newMilestoneText.trim(),
      completed: false,
      id: Date.now() + Math.random(),
    };
    setFormData({
      ...formData,
      milestones: [...formData.milestones, newMilestone],
    });
    setNewMilestoneText("");
  };

  const handleRemoveMilestone = (id) => {
    setFormData({
      ...formData,
      milestones: formData.milestones.filter((m) => m.id !== id),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title) return;

    const isMilestone = formData.type === "Milestone";

    if (isMilestone && formData.milestones.length === 0) {
      alert(t("goalsPage.milestoneError"));
      return;
    }

    if (!isMilestone && !formData.targetValue) return;

    try {
      await addGoal({
        ...formData,
        targetValue: isMilestone
          ? formData.milestones.length
          : Number(formData.targetValue),
        currentValue: isMilestone ? 0 : 0,
        status: "Active",
        createdAt: new Date().toISOString(),
        deadline: formData.deadline || null,
        milestones: isMilestone ? formData.milestones : [],
      });

      setFormData({
        title: "",
        type: "Financial",
        targetValue: "",
        deadline: "",
        milestones: [],
      });
      setNewMilestoneText("");
    } catch (err) {
      console.error(err);
    }
  };

  const isMilestoneType = formData.type === "Milestone";

  return (
    <FormContainer>
      <FormHeader>{t("goalsPage.addNewGoal")}</FormHeader>
      <form onSubmit={handleSubmit}>
        <FormGrid>
          <FormGroup>
            <Label htmlFor="title">{t("goalsPage.titleLabel")}</Label>
            <Input
              id="title"
              placeholder={t("goalsPage.titlePlaceholder")}
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="type">{t("goalsPage.type")}</Label>
            <Select id="type" value={formData.type} onChange={handleTypeChange}>
              <option value="Financial">{t("goalsPage.typeFinancial")}</option>
              <option value="Personal">{t("goalsPage.typePersonal")}</option>
              <option value="Milestone">{t("goalsPage.typeMilestone")}</option>
            </Select>
          </FormGroup>

          {!isMilestoneType && (
            <FormGroup>
              <Label htmlFor="target">{t("goalsPage.targetValue")}</Label>
              <Input
                id="target"
                type="number"
                placeholder="Amount/Count"
                value={formData.targetValue}
                onChange={(e) =>
                  setFormData({ ...formData, targetValue: e.target.value })
                }
                required
              />
            </FormGroup>
          )}

          <FormGroup
            style={{ gridColumn: isMilestoneType ? "span 1" : "auto" }}
          >
            <Label htmlFor="deadline">{t("goalsPage.deadline")}</Label>
            <Input
              id="deadline"
              type="date"
              value={formData.deadline}
              onChange={(e) =>
                setFormData({ ...formData, deadline: e.target.value })
              }
            />
          </FormGroup>
        </FormGrid>

        {isMilestoneType && (
          <MilestonesArea>
            <Label>
              {t("goalsPage.roadmapSteps")} ({formData.milestones.length}{" "}
              {t("goalsPage.defined")})
            </Label>
            <MilestoneInputGroup>
              <MilestoneInput
                type="text"
                placeholder={t("goalsPage.milestonePlaceholder")}
                value={newMilestoneText}
                onChange={(e) => setNewMilestoneText(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddMilestone();
                  }
                }}
              />
              <AddMilestoneButton type="button" onClick={handleAddMilestone}>
                {t("goalsPage.addStep")}
              </AddMilestoneButton>
            </MilestoneInputGroup>
            <MilestoneList>
              {formData.milestones.map((m) => (
                <MilestoneItem key={m.id}>
                  <span>{m.title}</span>
                  <RemoveMilestoneButton
                    type="button"
                    onClick={() => handleRemoveMilestone(m.id)}
                  >
                    {t("goalsPage.remove")}
                  </RemoveMilestoneButton>
                </MilestoneItem>
              ))}
            </MilestoneList>
          </MilestonesArea>
        )}

        <SubmitButton type="submit">{t("goalsPage.saveChanges")}</SubmitButton>
      </form>
    </FormContainer>
  );
};

// --- Modal Base Styles ---
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  transition: 0.5s;
`;

const ModalContent = styled(FormContainer)`
  width: 90%;
  max-width: 650px;
  margin: 0;
  position: relative;
  padding: 30px;
  animation: fadeIn 0.3s ease-out;
  transition: 0.5s;
  transition: 0.5s;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  color: var(--fontSecondaryColor);
  font-size: 1.5rem;
  cursor: pointer;
  line-height: 1;
  &:hover {
    color: var(--dangerRed);
  }
`;

// --- Edit Goal Modal Component ---
const EditGoalModal = ({ goal, onClose }) => {
  const { t } = useTranslation();
  const { updateGoal } = useContext(GoalsContext);
  const [formData, setFormData] = useState({
    title: goal.title,
    type: goal.type,
    targetValue: goal.targetValue || "",
    deadline: goal.deadline || "",
    milestones: goal.milestones || [],
  });
  const [newMilestoneText, setNewMilestoneText] = useState("");

  const handleTypeChange = (e) => {
    setFormData({
      ...formData,
      type: e.target.value,
      targetValue: e.target.value === "Milestone" ? "" : formData.targetValue,
    });
  };

  const handleAddMilestone = () => {
    if (newMilestoneText.trim() === "") return;
    const newMilestone = {
      title: newMilestoneText.trim(),
      completed: false,
      id: Date.now() + Math.random(),
    };
    setFormData({
      ...formData,
      milestones: [...formData.milestones, newMilestone],
    });
    setNewMilestoneText("");
  };

  const handleRemoveMilestone = (id) => {
    setFormData({
      ...formData,
      milestones: formData.milestones.filter((m) => m.id !== id),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title) return;

    const isMilestone = formData.type === "Milestone";
    const targetValue = isMilestone
      ? formData.milestones.length
      : Number(formData.targetValue);

    if (!isMilestone && !targetValue) return;

    const updates = {
      title: formData.title,
      type: formData.type,
      targetValue: targetValue,
      deadline: formData.deadline || null,
      milestones: isMilestone ? formData.milestones : [],
    };

    try {
      await updateGoal(goal.id, updates);
      onClose();
    } catch (err) {
      console.error(err);
    }
  };

  const isMilestoneType = formData.type === "Milestone";

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        <FormHeader>
          {t("goalsPage.editGoal")}: {goal.title}
        </FormHeader>
        <form onSubmit={handleSubmit}>
          <FormGrid>
            <FormGroup>
              <Label htmlFor="edit-title">{t("goalsPage.titleLabel")}</Label>
              <Input
                id="edit-title"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="edit-type">
                {t("goalsPage.typeChangeWarning")}
              </Label>
              <Select
                id="edit-type"
                value={formData.type}
                onChange={handleTypeChange}
              >
                <option value="Financial">
                  {t("goalsPage.typeFinancial")}
                </option>
                <option value="Personal">{t("goalsPage.typePersonal")}</option>
                <option value="Milestone">
                  {t("goalsPage.typeMilestone")}
                </option>
              </Select>
            </FormGroup>

            {!isMilestoneType && (
              <FormGroup>
                <Label htmlFor="edit-target">
                  {t("goalsPage.targetValue")}
                </Label>
                <Input
                  id="edit-target"
                  type="number"
                  placeholder="Amount/Count"
                  value={formData.targetValue}
                  onChange={(e) =>
                    setFormData({ ...formData, targetValue: e.target.value })
                  }
                  required
                />
              </FormGroup>
            )}

            <FormGroup
              style={{ gridColumn: isMilestoneType ? "span 1" : "auto" }}
            >
              <Label htmlFor="edit-deadline">{t("goalsPage.deadline")}</Label>
              <Input
                id="edit-deadline"
                type="date"
                value={formData.deadline}
                onChange={(e) =>
                  setFormData({ ...formData, deadline: e.target.value })
                }
              />
            </FormGroup>
          </FormGrid>

          {isMilestoneType && (
            <MilestonesArea>
              <Label>
                {t("goalsPage.roadmapSteps")} ({formData.milestones.length}{" "}
                {t("goalsPage.defined")})
              </Label>
              <MilestoneInputGroup>
                <MilestoneInput
                  type="text"
                  placeholder={t("goalsPage.milestonePlaceholder")}
                  value={newMilestoneText}
                  onChange={(e) => setNewMilestoneText(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddMilestone();
                    }
                  }}
                />
                <AddMilestoneButton type="button" onClick={handleAddMilestone}>
                  {t("goalsPage.addStep")}
                </AddMilestoneButton>
              </MilestoneInputGroup>
              <MilestoneList>
                {formData.milestones.map((m) => (
                  <MilestoneItem key={m.id}>
                    <span>{m.title}</span>
                    <RemoveMilestoneButton
                      type="button"
                      onClick={() => handleRemoveMilestone(m.id)}
                    >
                      {t("goalsPage.remove")}
                    </RemoveMilestoneButton>
                  </MilestoneItem>
                ))}
              </MilestoneList>
            </MilestonesArea>
          )}

          <SubmitButton type="submit">
            {t("goalsPage.saveChanges")}
          </SubmitButton>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

// --- Dopamine Splash Modal ---
const scaleAndFade = keyframes`
    0% { transform: scale(0); opacity: 0; }
    50% { transform: scale(1.2); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
`;

const sparkle = keyframes`
    0% { opacity: 0; transform: translate(0, 0) scale(0); }
    50% { opacity: 1; transform: translate(var(--x), var(--y)) scale(1); }
    100% { opacity: 0; transform: translate(var(--x), var(--y)) scale(0); }
`;

const SplashContent = styled(ModalContent)`
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 50px 30px;
  background: linear-gradient(135deg, var(--accentGreen), #3e8e47);
  color: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  overflow: hidden;
`;

const CheckMark = styled.div`
  font-size: 5rem;
  line-height: 1;
  margin-bottom: 20px;
  background: white;
  color: var(--accentGreen);
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${scaleAndFade} 0.5s cubic-bezier(0.68, -0.55, 0.27, 1.55);
`;

const SparkleContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
`;

const Sparkle = styled.div`
  position: absolute;
  width: 8px;
  height: 8px;
  background-color: ${(props) => props.color || "yellow"};
  border-radius: 50%;
  filter: blur(1px);
  animation: ${sparkle} 1s linear infinite;
  animation-delay: ${(props) => props.delay}s;
`;

const DopamineSplashModal = ({ goal, onClose }) => {
  const sparkles = Array.from({ length: 15 }).map((_, i) => ({
    key: i,
    x: `${Math.floor(Math.random() * 200) - 50}px`,
    y: `${Math.floor(Math.random() * 200) - 50}px`,
    delay: Math.random() * 0.5,
    color: ["#FFFF00", "#FFD700", "#FFFFFF", "#FF8C00"][
      Math.floor(Math.random() * 4)
    ],
  }));

  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <ModalOverlay>
      <SplashContent>
        <SparkleContainer>
          {sparkles.map((s) => (
            <Sparkle
              key={s.key}
              delay={s.delay}
              color={s.color}
              style={{
                "--x": s.x,
                "--y": s.y,
                top: "50%",
                left: "50%",
              }}
            />
          ))}
        </SparkleContainer>
        <CheckMark>✓</CheckMark>
        <FormHeader style={{ color: "white", fontSize: "2rem" }}>
          GOAL ACHIEVED!
        </FormHeader>
        <p style={{ margin: 0, fontSize: "1.2rem", fontWeight: 500 }}>
          "{goal.title}" is officially completed. Awesome work!
        </p>
      </SplashContent>
    </ModalOverlay>
  );
};

// --- GoalCard Styles ---
const Card = styled.div`
  background: ${(props) =>
    props.isCompleted ? "var(--lowGreen)" : "var(--secondaryBackgroundColor)"};
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  gap: 15px;
  border-left: 5px solid
    ${(props) =>
      props.isCompleted ? "var(--accentGreen)" : "var(--accentBlue)"};
  transition: all 0.5s ease;
  &:hover {
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.5);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--fontPrimaryColor);
  flex-grow: 1;
  transition: 0.5s;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const EditIcon = styled.button`
  background: none;
  border: none;
  color: var(--fontSecondaryColor);
  cursor: pointer;
  padding: 5px;
  font-size: 0.9rem;
  line-height: 1;
  opacity: 0.7;
  transition: color 0.2s, opacity 0.2s;
  &:hover {
    color: var(--accentBlue);
    opacity: 1;
  }
`;

const Badge = styled.span`
  background: var(--thirdBackgroundColor);
  padding: 4px 10px;
  border-radius: 15px;
  font-size: 0.7rem;
  color: var(--fontSecondaryColor);
  font-weight: bold;
  text-transform: uppercase;
`;

const ProgressBarContainer = styled.div`
  height: 6px;
  background: var(--thirdBackgroundColor);
  border-radius: 3px;
  overflow: hidden;
  margin: 0;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  width: ${(props) => props.percent}%;
  background: ${(props) => props.color};
  transition: width 0.8s ease-out;
  border-radius: 3px;
`;

const StatusText = styled.p`
  margin: 0;
  font-weight: 600;
  color: ${(props) => props.color};
  font-size: 0.95rem;
`;

const MetaData = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: var(--fontSecondaryColor);
  padding-top: 5px;
  border-top: 1px dashed var(--thirdBackgroundColor);
`;

const ManualInput = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 5px;
  padding-top: 10px;
  border-top: 1px solid var(--thirdBackgroundColor);
`;

const UpdateInput = styled(Input)`
  width: 120px;
  padding: 8px;
  font-size: 0.9rem;
`;

const UpdateButton = styled(SubmitButton)`
  padding: 8px 15px;
  font-size: 0.85rem;
  width: auto;
  background: var(--accentBlue);
  &:hover {
    background: #3a80c9;
  }
`;

const DeleteButton = styled.button`
  background: transparent;
  border: none;
  color: var(--fontSecondaryColor);
  cursor: pointer;
  font-size: 0.8rem;
  align-self: flex-end;
  margin-top: 5px;
  padding: 0;
  transition: color 0.2s;
  &:hover {
    color: var(--dangerRed);
  }
`;

const RoadmapContainer = styled.div`
  padding: 10px 0;
  margin-top: 10px;
  border-top: 1px solid var(--thirdBackgroundColor);
  display: flex;
  flex-direction: column;
  gap: 15px;
  position: relative;
  max-height: 250px;
  overflow-y: auto;
  padding-right: 15px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--thirdBackgroundColor);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: var(--secondaryBackgroundColor);
  }
`;

const RoadmapLine = styled.div`
  position: absolute;
  left: 20px;
  top: 20px;
  bottom: 20px;
  width: 2px;
  background: var(--thirdBackgroundColor);
  height: 100%;
`;

const RoadmapProgress = styled.div`
  position: absolute;
  left: 20px;
  top: 20px;
  width: 2px;
  background: var(--accentGreen);
  height: ${(props) => props.percent}%;
  transition: height 0.8s ease-out;
`;

const RoadmapMilestone = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  padding-left: 50px;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  opacity: ${(props) => (props.completed ? 0.8 : 1)};
  transition: opacity 0.2s;

  &:hover {
    opacity: ${(props) => (props.disabled ? 0.8 : 0.9)};
  }
`;

const RoadmapIndicator = styled.div`
  position: absolute;
  left: 12px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${(props) =>
    props.completed ? "var(--accentGreen)" : "var(--secondaryBackgroundColor)"};
  border: 2px solid
    ${(props) =>
      props.completed ? "var(--accentGreen)" : "var(--fontSecondaryColor)"};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: white;
  font-weight: bold;
  box-shadow: 0 0 0 4px var(--secondaryBackgroundColor);
`;

const MilestoneDetails = styled.div`
  font-size: 0.95rem;
  font-weight: ${(props) => (props.completed ? "400" : "500")};
  color: ${(props) =>
    props.completed ? "var(--fontSecondaryColor)" : "var(--fontPrimaryColor)"};
  text-decoration: ${(props) => (props.completed ? "line-through" : "none")};
  padding: 5px 0;
`;

// --- GoalCard Component ---
const GoalCard = ({ goal, onEdit }) => {
  const { t } = useTranslation();
  const { updateManualProgress, deleteGoal, toggleMilestone } =
    useContext(GoalsContext);
  const [manualVal, setManualVal] = useState(goal.currentValue || 0);

  useEffect(() => {
    setManualVal(goal.currentValue || 0);
  }, [goal.currentValue]);

  const message = getSmartMessage(goal, t);

  let percent, current, target;

  if (goal.type === "Milestone") {
    const completed = (goal.milestones || []).filter((m) => m.completed).length;
    const total = (goal.milestones || []).length || 1;
    percent = Math.min(100, Math.round((completed / total) * 100));
    current = completed;
    target = total;
  } else {
    percent = Math.min(
      100,
      Math.round(((goal.currentValue || 0) / (goal.targetValue || 1)) * 100)
    );
    current = goal.currentValue;
    target = goal.targetValue;
  }

  const handleUpdate = async () => {
    try {
      await updateManualProgress(goal.id, manualVal);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t("goalsPage.confirmDelete"))) return;
    try {
      await deleteGoal(goal.id);
    } catch (err) {
      console.error(err);
    }
  };

  const handleMilestoneToggle = (index) => {
    toggleMilestone(goal.id, index);
  };

  return (
    <Card isCompleted={goal.status === "Completed"}>
      <CardHeader>
        <CardTitle>{goal.title}</CardTitle>
        <CardActions>
          <Badge>
            {goal.type === "Milestone" ? t("goalsPage.milestones") : goal.type}
          </Badge>
          <EditIcon onClick={() => onEdit(goal)}>
            <span role="img" aria-label="Edit">
              ✏️
            </span>
          </EditIcon>
        </CardActions>
      </CardHeader>

      <StatusText color={message.color}>{message.text}</StatusText>

      <ProgressBarContainer>
        <ProgressBarFill percent={percent} color={message.color} />
      </ProgressBarContainer>

      <MetaData>
        <span>
          {goal.type === "Milestone"
            ? t("goalsPage.stepsCompleted")
            : t("goalsPage.currentValue")}
          : {(Number(current) || 0).toFixed(0)} / {t("goalsPage.targetValue")}:{" "}
          {(Number(target) || 0).toFixed(0)}
        </span>
        <span>{percent}%</span>
      </MetaData>

      {goal.type === "Personal" && goal.status !== "Completed" && (
        <ManualInput>
          <UpdateInput
            type="number"
            value={manualVal}
            onChange={(e) => setManualVal(e.target.value)}
            placeholder={t("goalsPage.newValue")}
          />
          <UpdateButton onClick={handleUpdate}>
            {t("goalsPage.update")}
          </UpdateButton>
        </ManualInput>
      )}

      {goal.type === "Milestone" &&
        goal.milestones &&
        goal.milestones.length > 0 && (
          <RoadmapContainer>
            <RoadmapLine />
            <RoadmapProgress percent={percent} />

            {goal.milestones.map((m, index) => (
              <RoadmapMilestone
                key={m.id || index}
                onClick={() => handleMilestoneToggle(index)}
              >
                <RoadmapIndicator completed={m.completed}>
                  {m.completed ? "✓" : index + 1}
                </RoadmapIndicator>
                <MilestoneDetails completed={m.completed}>
                  {m.title}
                </MilestoneDetails>
              </RoadmapMilestone>
            ))}
          </RoadmapContainer>
        )}

      <DeleteButton onClick={handleDelete}>
        {t("goalsPage.removeGoal")}
      </DeleteButton>
    </Card>
  );
};

// --- Dashboard Layout
const PageContainer = styled.div`
  padding: 40px 20px;
  max-width: 1200px;
  font-family: "Montserrat", sans-serif;
  margin: 0 auto;
  color: var(--fontPrimaryColor);
  h1 {
    margin-bottom: 1em;
    font-size: 2.5rem;
    font-weight: 800;
  }

  @media (max-width: 500px) {
    margin: 0 auto;
    padding: 30px 0px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 25px;
  @media (max-width: 400px) {
    grid-template-columns: 1fr;
  }
`;

const SectionTitle = styled.h2`
  margin-top: 40px;
  margin-bottom: 20px;
  color: var(--fontPrimaryColor);
  font-size: 1.8rem;
  font-weight: 700;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: var(--fontSecondaryColor);
  font-size: 1.1rem;
  background: var(--secondaryBackgroundColor);
  border-radius: 12px;
  font-family: "Montserrat", sans-serif;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
`;

const FullContainer = styled.div`
  @media (max-width: 650px) {
    margin-top: 120px;
  }
`;

// =========================================================
// VII. MAIN DASHBOARD
// ===================================
// ======================
const GrowthGoals = ({ theme }) => {
  const { t } = useTranslation();
  const {
    goals,
    loading: goalsLoading,
    justCompletedGoal,
    resetDopamineSplash,
  } = useContext(GoalsContext);
  const [editingGoal, setEditingGoal] = useState(null);
  const { loading: financeLoading } = useContext(FinanceContext);

  if (financeLoading || goalsLoading)
    return (
      <PageContainer>
        <h1>{t("goalsPage.loading")}</h1>
      </PageContainer>
    );

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
  };

  const handleCloseEdit = () => {
    setEditingGoal(null);
  };

  return (
    <FullContainer>
      <GlobalStyle />
      {/* <Navbar theme={theme} /> */}
      <Header>
        <Title>{t("goalsPage.title")}</Title>
        <Subtitle>{t("goalsPage.subtitle")}</Subtitle>
      </Header>
      <PageContainer>
        <GoalForm />
        <SectionTitle>{t("goalsPage.goalsSection")}</SectionTitle>
        {!goals || goals.length === 0 ? (
          <EmptyState>{t("goalsPage.noGoals")}</EmptyState>
        ) : (
          <Grid>
            {goals.map((goal) => (
              <GoalCard key={goal.id} goal={goal} onEdit={handleEditGoal} />
            ))}
          </Grid>
        )}
      </PageContainer>

      {editingGoal && (
        <EditGoalModal goal={editingGoal} onClose={handleCloseEdit} />
      )}

      {justCompletedGoal && (
        <DopamineSplashModal
          goal={justCompletedGoal}
          onClose={resetDopamineSplash}
        />
      )}
    </FullContainer>
  );
};

// =========================================================
// VIII. APP WRAPPER
// =========================================================
const App = ({ theme }) => (
  <FinanceProvider>
    <MockExternalProvider>
      <GoalsProvider>
        <GrowthGoals theme={theme} />
      </GoalsProvider>
    </MockExternalProvider>
  </FinanceProvider>
);

export default App;
